<template>
  <div class="cart">
    <h1 class="title">My Cart</h1>

    <p
      class="text-center"
      v-if="cart_items.length == 0"
    >Your cart is empty!</p>

    <b-container
      v-else
      class="mt-4"
    >
      <b-row class="border-bottom">
        <b-col cols="6">
          <h5>Item Details</h5>
        </b-col>

        <b-col cols="2">
          <h5>Price</h5>
        </b-col>

        <b-col cols="2">
          <h5>Quantity</h5>
        </b-col>

        <b-col cols="2">
          <h5>Total</h5>
        </b-col>
      </b-row>

      <b-row
        v-for="cart_item in cart_items"
        :key="cart_item.id"
        class="mt-4 border-bottom font-lighter"
      >
        <b-col cols="6">
          <b-media>
            <template v-slot:aside>
              <b-img
                width="128"
                height="100"
                alt="placeholder"
                :src="cart_item.attributes.menu_item_image_url"
                class="mb-4"
              ></b-img>
            </template>

            <h5 class="font-lighter">{{ cart_item.attributes.menu_item_name }}</h5>

            <h6
              v-if="cart_item.attributes.add_ons_total > 0"
              class="font-lighter"
            >Add Ons</h6>
            <b-link @click="remove_item(cart_item.id)" class="text-danger">Remove</b-link>
          </b-media>
        </b-col>

        <b-col cols="2" class="mt-1">
          <h6 class="font-lighter">
            $ {{ parseFloat(cart_item.attributes.price).toFixed(2) }}
          </h6>

          <h6
            v-if="cart_item.attributes.add_ons_total > 0"
            class="font-lighter"
          >$ {{ parseFloat(cart_item.attributes.add_ons_total).toFixed(2) }}</h6>
        </b-col>

        <b-col cols="2">
          <number-input
            :min="1"
            inline
            controls
            :value=1
            size="small"
            v-model="cart_item.attributes.quantity"
            @change="update_quantity($event, cart_item)"
          ></number-input>
        </b-col>

        <b-col cols="2">$ {{ parseFloat(cart_item.attributes.total).toFixed(2) }}</b-col>
      </b-row>

      <b-row class="last-row">
        <b-col cols="8"></b-col>

        <b-col cols="4" class="font-lighter">
          <b-row>
            <b-col>
              <p>Subtotal</p>
            </b-col>

            <b-col class="text-right">
              <p>$ {{ subtotal }}</p>
            </b-col>
          </b-row>

          <b-row class="border-bottom">
            <b-col>
              <p>Delivery</p>
            </b-col>

            <b-col class="text-right">
              <p>$ {{ delivery_charges }}</p>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col>
              <h5 class="font-lighter">Total</h5>
            </b-col>

            <b-col class="text-right">
              <h5 class="font-lighter">$ {{ grand_total }}</h5>
            </b-col>
          </b-row>

          <b-button
            squared
            variant="outline-primary"
            class="w-100"
            @click="show_address_modal"
          >Continue</b-button>
        </b-col>
      </b-row>
    </b-container>

    <b-modal v-show="addresses_fetched" ref="address-modal" title="Place Your Order" hide-footer centered>
      <h6>Delivery Address</h6>

      <b-row v-if="addresses_fetched" class="justify-content-center border-bottom">
        <b-col cols="6" class="mb-4">
          <p class="text-secondary mb-1">Select Address:</p>

          <b-form-select v-model="selectedAddress">
            <option value="null" disabled>Select address</option>

            <option
              v-for="address in addresses"
              :key="address.id"
              :value="address"
            >
              {{ complete_address(address.attributes) }}
            </option>
          </b-form-select>

          <span v-if="show_error && selectedAddress == null" class="form-error">Can't be blank</span>
        </b-col>
      </b-row>

      <h6>Payment</h6>

      <b-row v-if="cardsFetched" class="justify-content-center border-bottom">
        <b-col cols="6" class="mb-4">
          <p class="text-secondary mb-1">Select Card:</p>

          <b-form-select v-model="card_id">
            <option value="null" disabled>Select card</option>

            <option
              v-for="card in cards"
              :key="card.id"
              :value="card.attributes.stripe_id"
            >
              **** **** **** {{ card.attributes.last4 }} {{ card.attributes.brand }}
            </option>
          </b-form-select>

          <span v-if="show_error && card_id == ''" class="form-error">Can't be blank</span>
        </b-col>
      </b-row>


      <b-row class="float-right mt-3 mr-1">
        <b-button
          squared
          variant="outline-primary"
          @click="place_order"
          :disabled="isPaymentCreated"
        >Checkout
          <b-spinner v-if="isPaymentCreated" class="btn-spinner ml-2"></b-spinner>
        </b-button>
      </b-row>
    </b-modal>

    <customer-modal></customer-modal>
  </div>
</template>

<script>
import CustomerModal from "../components/customer_modal";
import { getCards, isValidOrder, createOrderPayment, isValidAddress } from '../store/api'
import { mapGetters } from 'vuex'

export default {
  name: 'cart',

  components: {
    'customer-modal': CustomerModal,
  },

  data() {
    return {
      cart_items_count: 0,
      addresses_fetched: false,
      selectedAddress: null,
      show_error: false,
      cards: [],
      cardsFetched: false,
      card_id: '',
      isPaymentCreated: false,
    }
  },

  created() {
    if (this.logged_in) {
      getCards()
        .then(response => {
          this.cardsFetched = true
          this.cards = response.data
        })
    }
    this.$store.dispatch("load_cart_items", {
      order_id: $cookies.get('order_id')
    });
  },

  computed: {
    ...mapGetters(['logged_in']),
    cart_items() {
      return this.$store.state.cart_items
    },
    subtotal() {
      return parseFloat(this.$store.getters.cart_total).toFixed(2)
    },
    delivery_charges() {
      return (parseFloat(this.$store.getters.delivery_charges) || 0).toFixed(2)
    },
    grand_total() {
      return (parseFloat(this.subtotal) + parseFloat(this.delivery_charges)).toFixed(2)
    },
    addresses() {
      return this.$store.state.current_customer.addresses
    },
    complete_address() {
      return address => [address.street, address.building, address.floor, address.area].join(', ')
    },
  },

  methods: {
    remove_item(cart_item_id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      })
      .then((result) => {
        if (result.value) {
          this.$store.dispatch("remove_cart_item", {
            order_id: $cookies.get('order_id'),
            order_item_id: cart_item_id
          });
          this.$swal('Removed!', 'Item has been removed from cart.', 'success')
        }
      })
    },
    update_quantity(new_quantity, cart_item) {
      if (this.cart_items_count >= this.cart_items.length) {
        this.$store.dispatch("update_cart_item", {
          order_id: $cookies.get('order_id'),
          cart: {
            order_items_attributes: [{
              id: cart_item.id,
              quantity: new_quantity
            }]
          }
        })
      }
      else
        this.cart_items_count += 1
    },
    place_order() {
      if (this.selectedAddress == null || this.card_id == '') {
        this.show_error = true
      } else {
        this.isPaymentCreated = true
        isValidOrder({ address_id: this.selectedAddress.id })
          .then(orderResponse => {
            if (orderResponse.is_valid == true) {
              createOrderPayment({
                order_payment: {
                  amount: this.grand_total
                },
                card_id: this.card_id
              })
                .then(paymentResponse => {
                  isValidAddress({
                    latitude: this.selectedAddress.attributes.latitude,
                    longitude: this.selectedAddress.attributes.longitude
                  })
                    .then((validAddressResponse) => {
                      if (validAddressResponse.result) {
                        this.isPaymentCreated = false
                        this.$refs['address-modal'].hide()
                        this.$store.dispatch('place_order', {
                          cart: {
                            status: 1,
                            address_id: this.selectedAddress.id
                          }
                        })
                        this.$notify({
                          type: 'success',
                          text: 'Your order has been placed successfuly.'
                        })
                        this.card_id = ''
                        this.selectedAddress = null
                        this.show_error = false
                      } else {
                        this.isPaymentCreated = false
                        this.$notify({
                          type: 'error',
                          text: 'Sorry, your address is not in our range.'
                        })
                      }
                    })
                })
            } else {
              this.isPaymentCreated = false
              this.$notify({
                type: 'error',
                text: 'Your order is not valid, so you\'re unable to place order.'
              })
            }
          })
          .catch(orderError => {
            this.isPaymentCreated = false
            this.$notify({
              type: 'error',
              text: 'Something went wrong! Please try again.'
            })
          })
      }
    },
    show_address_modal() {
      if(this.$store.getters.logged_in) {
        this.$store.dispatch('load_addresses')
          .then(response => {
            this.addresses_fetched = true
            this.$refs['address-modal'].show()
          })
      } else {
        this.$root.$emit('bv::show::modal', 'customer_modal')
      }
    }
  },

  beforeCreate: function() {
    document.body.className = '';
  },
}
</script>

<style scoped>
  h1 {
    text-align: center;
  }
  .btn-sm {
    font-size: 0.75rem;
  }
  .font-bold {
    font-weight: bold;
  }
  .last-row {
    margin: 20px auto;
  }
  .btn-outline-primary {
    color: #5663FF;
    border-color: #5663FF;
    background-color: white;
    box-shadow: none;
  }
  .btn-outline-primary:hover {
    color: white;
    border-color: #5663FF;
    background-color: #5663FF;
  }
  .dropdown-menu li {
    font-size: 0.8rem;
  }
  .form-error {
    color: #D9534F;
    padding: 5px;
  }
  .error-input {
    border:1px solid #D9534F
  }
  .btn-spinner {
    height: 20px;
    width: 20px;
  }
</style>
